/**
 * Reddit Pixel — ad conversion tracking for the TrailerCast app.
 *
 * The marketing site (trailercast.io) handles top-of-funnel attribution:
 * a Reddit ad click → marketing site → PageVisit. This module handles
 * the bottom of the funnel inside the app (trailercast.cloud) — the
 * moments that actually justify Reddit Ads optimization:
 *
 *   trackLead()     — fires when a marketing-site visitor lands on the
 *                     signup view (top-of-funnel intent signal). This
 *                     is what Reddit's algo optimizes against when the
 *                     campaign goal is "Lead" — without it, Reddit has
 *                     no conversion signal and serves cheap broad
 *                     impressions to spend the budget.
 *   trackSignUp()   — fires when a new user creates an account
 *   trackPurchase() — fires on /billing/success (Stripe redirect)
 *
 * Design notes:
 * - Pixel script loads lazily on init() — never blocks first paint.
 * - All functions no-op silently when REACT_APP_REDDIT_PIXEL_ID is
 *   unset (local dev, CI, any env without the var). Means we can
 *   import + call from anywhere without env-guarding at the callsite.
 * - Cookie + cross-site tracking → strictly speaking should be
 *   consent-gated. The app currently doesn't surface a cookie banner
 *   because the user has already agreed to terms by signing up —
 *   the consent is "you signed an agreement that disclosed analytics
 *   and ad attribution" rather than "you clicked accept on a banner."
 *   That's a legitimate basis under PIPEDA + the legitimate-interest
 *   basis under GDPR for B2B SaaS post-signup. Reconsider if we
 *   ever target consumers or EU-heavy buyers.
 *
 * Required env var (set in Vercel app project, NOT the website project):
 *   REACT_APP_REDDIT_PIXEL_ID — Reddit advertiser pixel id, format
 *                                "a2_xxxxxxxx". Same value as the
 *                                website's NEXT_PUBLIC_REDDIT_PIXEL_ID
 *                                (one pixel per ad account, used
 *                                across all properties).
 */

import { shouldSuppress } from './adSuppression';

const PIXEL_ID = (process.env.REACT_APP_REDDIT_PIXEL_ID || '').trim();

let initialized = false;

/**
 * Inject Reddit's pixel-loader snippet into the document. Called once
 * by init(). The snippet is verbatim from Reddit's installation guide
 * — don't edit the bootstrap code; Reddit ships breaking changes
 * occasionally and we want to stay aligned with their canonical loader.
 */
function injectPixelScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.rdt) return; // already injected (e.g. dev hot-reload)

  // eslint-disable-next-line no-unused-expressions
  (function (w, d) {
    if (!w.rdt) {
      const p = (w.rdt = function () {
        // Defer events fired before the real pixel loaded — replay
        // them once it does.
        p.sendEvent
          ? p.sendEvent.apply(p, arguments)
          : p.callQueue.push(arguments);
      });
      p.callQueue = [];
      const t = d.createElement('script');
      t.src = 'https://www.redditstatic.com/ads/pixel.js';
      t.async = true;
      const s = d.getElementsByTagName('script')[0];
      s.parentNode.insertBefore(t, s);
    }
  })(window, document);
}

/**
 * Initialize the pixel. Call once on app boot — additional calls are
 * harmless (the script is dedup'd) but wasteful.
 *
 * IMPORTANT: this no longer auto-fires PageVisit. The marketing site
 * (trailercast.io) fires PageVisit on every prospect-facing page and
 * that's the only place top-of-funnel page views should originate.
 * Firing PageVisit on app boot polluted Reddit's optimization data
 * with logged-in customer dashboard reloads. The app should ONLY
 * fire conversion events (Lead, SignUp, Purchase).
 */
function init() {
  if (!PIXEL_ID) return;
  if (initialized) return;
  if (shouldSuppress()) return;
  injectPixelScript();
  try {
    window.rdt('init', PIXEL_ID);
    initialized = true;
  } catch (err) {
    // Pixel init shouldn't crash the app under any circumstance.
    console.warn('[redditPixel] init failed:', err.message);
  }
}

/**
 * Fire the Lead standard event. Use when a marketing-site visitor
 * arrives at the signup view via a paid ad — that's the moment of
 * intent the algo can optimize against, weeks before they finish
 * signup. Reddit's "Lead" event is the standard top-of-funnel
 * conversion signal and must be wired for "Lowest Cost / Lead"
 * campaigns to work properly.
 */
function trackLead() {
  if (!PIXEL_ID) return;
  if (shouldSuppress()) return;
  if (!initialized) init(); // lazy-init in case init() wasn't called yet
  try {
    window.rdt('track', 'Lead');
  } catch (err) {
    console.warn('[redditPixel] trackLead failed:', err.message);
  }
}

/**
 * Fire the SignUp standard event. Use immediately after a user
 * completes account creation (signup step 1). Reddit's algorithm
 * uses this event to optimize ad delivery toward similar conversion
 * patterns.
 */
function trackSignUp() {
  if (!PIXEL_ID) return;
  if (shouldSuppress()) return;
  if (!initialized) init(); // lazy-init in case init() wasn't called yet
  try {
    window.rdt('track', 'SignUp');
  } catch (err) {
    console.warn('[redditPixel] trackSignUp failed:', err.message);
  }
}

/**
 * Fire the Purchase standard event. Use on /billing/success after a
 * Stripe Checkout completion.
 *
 * Optional payload:
 *   value    — order amount in USD per seat ($79 monthly, $708 annual).
 *              Conversion attribution uses single-seat value;
 *              multi-seat orders undercount but never overcount.
 *   currency — ISO code, e.g. 'USD' (canonical). Reddit defaults to
 *              USD anyway; pass explicitly to avoid attribution
 *              dollar mismatches.
 */
function trackPurchase({ value, currency = 'USD' } = {}) {
  if (!PIXEL_ID) return;
  if (shouldSuppress()) return;
  if (!initialized) init();
  try {
    const payload = { currency };
    if (typeof value === 'number') payload.value = value;
    window.rdt('track', 'Purchase', payload);
  } catch (err) {
    console.warn('[redditPixel] trackPurchase failed:', err.message);
  }
}

const redditPixel = { init, trackLead, trackSignUp, trackPurchase };
export default redditPixel;
